/**
 * @author Jesse Rogers <jesse.rogers@adaptiva.com>
 * @fileoverview Component to allow drag and drop organization for office hierarchy
 */

import { DisplayValue, Generators } from '@adaptiva/core/common';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { DynamicOverlay, OverlayService } from '@adaptiva/core/overlays';
import { TreeNode } from '@adaptiva/core/trees';
import { EJavaClassNames, IOffice, OFFICE_SEARCH_BY_IP_TOKEN } from '@adaptiva/data-service';
import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { switchMap, take } from 'rxjs';

@Component({
	standalone: false,
	selector: 'ada-location-hierarchy',
	templateUrl: './location-hierarchy.component.html',
	styleUrls: ['./location-hierarchy.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{
		provide: OFFICE_SEARCH_BY_IP_TOKEN,
		useFactory: () => signal(false)
	}]
})
export class LocationHierarchyComponent {

	public readonly id: string = Generators.getStringId();

	public readonly objectClassName: EJavaClassNames = EJavaClassNames.Office;

	public searchOptions: DisplayValue[] = [
		new DisplayValue('Display name', false),
		new DisplayValue('IP Address', true)
	];

	protected readonly searchByIp: WritableSignal<boolean> = inject(OFFICE_SEARCH_BY_IP_TOKEN);

	protected readonly overlays: OverlayService = inject(OverlayService);

	protected readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);
	
	protected isOverlayOpen: boolean = false;

	public onSearchOptionChange(_searchByIp: boolean): void {
		this.searchByIp.set(_searchByIp);
	}

	public nodeClicked(_node: TreeNode<IOffice>): void {
		if (this.isOverlayOpen)
			return;

		this.isOverlayOpen = true;

		this.overlays.load({
			overlayId: 'ViewClientsActionOverlayComponent',
			context: _node.data
		}).pipe(
			switchMap((_overlay: DynamicOverlay) => _overlay.submit),
			take(1)
		).subscribe({
			next: (): void => {
				this.isOverlayOpen = false;
			},
			error: (e: Error): void => {
				this.isOverlayOpen = false;
				this.errorHandler.defaultErrorHandler(e, 'LocationHierarchyComponent');
			}
		});
	}

}
